import * as React from "react";
import { FileText, LoaderCircle, Upload } from "lucide-react";
import { cn } from "../../lib/utils";
import { buttonVariants } from "./button";

type FileUploadProps = {
  id: string;
  label: string;
  hint: string;
  browseLabel: string;
  changeLabel: string;
  busyLabel: string;
  filename?: string;
  disabled?: boolean;
  uploading?: boolean;
  compact?: boolean;
  onFileSelect: (file: File) => void;
};

export function FileUpload({
  id,
  label,
  hint,
  browseLabel,
  changeLabel,
  busyLabel,
  filename,
  disabled,
  uploading,
  compact,
  onFileSelect,
}: FileUploadProps) {
  const [dragging, setDragging] = React.useState(false);
  const [pendingName, setPendingName] = React.useState("");
  const dragDepth = React.useRef(0);
  const blocked = disabled || uploading;
  const selectedName = uploading ? pendingName || filename : filename;
  const choose = (file?: File) => {
    if (!file || blocked) return;
    setPendingName(file.name);
    onFileSelect(file);
  };
  return (
    <div
      className="vl-file-upload"
      data-dragging={(dragging && !blocked) || undefined}
      data-disabled={blocked || undefined}
      data-compact={compact || undefined}
      aria-busy={uploading || undefined}
      onDragEnter={(event) => {
        event.preventDefault();
        if (event.dataTransfer.types.includes("Files")) {
          dragDepth.current += 1;
          if (!blocked) setDragging(true);
        }
      }}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = blocked ? "none" : "copy";
      }}
      onDragLeave={(event) => {
        event.preventDefault();
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setDragging(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        dragDepth.current = 0;
        setDragging(false);
        choose(event.dataTransfer.files[0]);
      }}
    >
      <input
        id={id}
        className="vl-file-input"
        type="file"
        accept=".pdf,.doc,.docx"
        disabled={blocked}
        aria-label={label}
        aria-describedby={`${id}-hint`}
        onChange={(event) => {
          choose(event.currentTarget.files?.[0]);
          // Allow retrying or replacing with the same file after a failed upload.
          event.currentTarget.value = "";
        }}
      />
      <label htmlFor={id} className="vl-file-label">
        <span className="vl-file-icon" aria-hidden="true">
          {uploading ? (
            <LoaderCircle size={20} className="vl-file-spinner" />
          ) : selectedName ? (
            <FileText size={20} />
          ) : (
            <Upload size={20} />
          )}
        </span>
        <span className="vl-file-copy">
          <span className="vl-file-title">{selectedName || label}</span>
          <span id={`${id}-hint`} className="vl-file-hint">
            {hint}
          </span>
        </span>
        <span
          aria-hidden="true"
          className={cn(
            buttonVariants({ variant: "outline", size: "sm" }),
            "vl-file-browse",
          )}
        >
          {uploading ? busyLabel : filename ? changeLabel : browseLabel}
        </span>
      </label>
      <span className="vl-sr-only" role="status">
        {uploading ? busyLabel : filename || ""}
      </span>
    </div>
  );
}
